import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoaderConfig {
  message?: string;
  subMessage?: string;
  showLogo?: boolean;
  variant?: 'fullscreen' | 'overlay' | 'inline';
}

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _config$ = new BehaviorSubject<LoaderConfig>({
    message: 'Rolling the Reel',
    subMessage: 'Preparing your cinematic experience',
    showLogo: true,
    variant: 'fullscreen',
  });
  private activeRequests = 0;
  private manualLoading = false;
  private manualTimeout: any = null;
  private autoResetTimeout: any = null;

  readonly loading$: Observable<boolean> = this._loading$.asObservable();
  readonly config$: Observable<LoaderConfig> = this._config$.asObservable();

  private updateLoading(): void {
    const shouldLoad = this.manualLoading || this.activeRequests > 0;
    if (this._loading$.getValue() !== shouldLoad) {
      this._loading$.next(shouldLoad);
    }
  }

  show(config?: LoaderConfig): void {
    if (config) {
      this._config$.next({ ...this._config$.getValue(), ...config });
    }
    this.activeRequests += 1;
    this.updateLoading();

    // Sanity safety: auto-reset if any request is stuck for more than 6s
    if (this.autoResetTimeout) {
      clearTimeout(this.autoResetTimeout);
    }
    this.autoResetTimeout = setTimeout(() => {
      if (this.activeRequests > 0) {
        this.activeRequests = 0;
        this.updateLoading();
      }
    }, 6000);
  }

  hide(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeRequests === 0 && this.autoResetTimeout) {
      clearTimeout(this.autoResetTimeout);
      this.autoResetTimeout = null;
    }
    this.updateLoading();
  }

  showManual(config?: LoaderConfig): void {
    if (config) {
      this._config$.next({ ...this._config$.getValue(), ...config });
    }
    this.manualLoading = true;
    this.updateLoading();

    // Auto-dismiss safety guard: Never allow manual loader to freeze user interaction
    if (this.manualTimeout) {
      clearTimeout(this.manualTimeout);
    }
    this.manualTimeout = setTimeout(() => {
      this.hideManual();
    }, 2500);
  }

  hideManual(): void {
    if (this.manualTimeout) {
      clearTimeout(this.manualTimeout);
      this.manualTimeout = null;
    }
    this.manualLoading = false;
    this.updateLoading();
  }

  forceReset(): void {
    if (this.manualTimeout) {
      clearTimeout(this.manualTimeout);
      this.manualTimeout = null;
    }
    if (this.autoResetTimeout) {
      clearTimeout(this.autoResetTimeout);
      this.autoResetTimeout = null;
    }
    this.manualLoading = false;
    this.activeRequests = 0;
    this.updateLoading();
  }

  get isLoading(): boolean {
    return this._loading$.getValue();
  }

  setMessage(message: string, subMessage?: string): void {
    this._config$.next({ ...this._config$.getValue(), message, subMessage });
  }
}
